import type { ReactNode } from "react";

import {
  getEnumLabel,
  getGenderOptions,
  getLanguageOptions,
} from "Forms/Utils/EnumOptions";
import type { ActorDetailDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { translateCountryCode } from "Infrastructure/Utils/AddressUtils";
import {
  type InformationSection,
  InformationView,
} from "Shared/InformationView";
import { formatDate } from "Shared/Utils/Format/date";
import {
  formatIbanDisplay,
  getFormattedValue,
  phoneMask,
} from "Shared/Utils/Format/object";

interface ActorInformationProps {
  actor: ActorDetailDto;
  onEdit?: () => void;
}

export const ActorInformation = ({
  actor,
  onEdit,
}: ActorInformationProps): ReactNode => {
  const { t } = useResource();

  const birthDateFormatted = formatDate(actor.birthDate) || undefined;

  const industryLabel =
    actor.industries?.length > 0 ? actor.industries.join(", ") : undefined;

  const billingAddress = actor.billingDetails?.address
    ? [
        actor.billingDetails.address.street,
        `${actor.billingDetails.address.postalCode} ${actor.billingDetails.address.municipality}`,
        translateCountryCode(actor.billingDetails.address.countryCode, t),
      ]
        .filter(Boolean)
        .join(", ")
    : undefined;

  const sections: InformationSection[] = [
    {
      items: [
        { label: t(Resources.Actors.ActorDetail.Email), value: actor.email },
        {
          label: t(Resources.Actors.ActorDetail.Phone),
          value: getFormattedValue(actor.phone, undefined, phoneMask),
        },
        {
          label: t(Resources.Actors.ActorDetail.DateOfBirth),
          value: birthDateFormatted,
        },
        {
          label: t(Resources.Actors.ActorDetail.Gender),
          value: getEnumLabel(getGenderOptions(), actor.gender),
        },
        {
          label: t(Resources.Actors.ActorDetail.NativeLanguage),
          value: getEnumLabel(getLanguageOptions(), actor.language),
        },
        {
          label: t(Resources.Actors.ActorDetail.Industry),
          value: industryLabel,
        },
        {
          label: t(Resources.Actors.ActorDetail.Address),
          value: actor.address?.street,
        },
        {
          label: t(Resources.Actors.ActorDetail.City),
          value: actor.address?.municipality,
        },
        {
          label: t(Resources.Actors.ActorDetail.PostalCode),
          value: actor.address?.postalCode,
        },
        {
          label: t(Resources.Actors.ActorDetail.State),
          value: translateCountryCode(actor.address?.countryCode, t),
        },
        {
          label: t(Resources.Actors.ActorDetail.IBAN),
          value: actor.iban ? formatIbanDisplay(actor.iban) : undefined,
        },
        { label: t(Resources.Actors.ActorDetail.SWIFT), value: actor.swift },
      ],
    },
    ...(actor.billingDetails?.companyName
      ? [
          {
            title: t(Resources.Actors.CreateActorModal.Invoicing),
            items: [
              {
                label: t(Resources.Actors.ActorDetail.BusinessName),
                value: actor.billingDetails.companyName,
              },
              {
                label: t(Resources.Actors.ActorDetail.CompanyNumber),
                value: actor.billingDetails.companyNumber,
              },
              {
                label: t(Resources.Actors.ActorDetail.TaxNumber),
                value: actor.billingDetails.taxNumber,
              },
              {
                label: t(Resources.Actors.ActorDetail.BusinessAddress),
                value: billingAddress,
              },
              {
                label: t(Resources.Actors.ActorDetail.BillingIBAN),
                value: actor.billingDetails.iban
                  ? formatIbanDisplay(actor.billingDetails.iban)
                  : undefined,
              },
              {
                label: t(Resources.Actors.ActorDetail.BillingSWIFT),
                value: actor.billingDetails.swift,
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <InformationView
      title={t(Resources.Actors.ActorDetail.Information)}
      editLabel={t(Resources.Actors.ActorDetail.EditInformation)}
      onEdit={onEdit}
      sections={sections}
    />
  );
};
